// Test-ids for the marketplace surfaces QA drives headlessly. Registered in
// ~/testing as `dt.marketplace.*`.
export default {
  // The full-bleed page banner + its nav tab bar. Mantine class hashes make positional assertions
  // unstable, so QA anchors on these. Each nav tab id is suffixed by its value
  // (nav.tab + '-' + value) and carries data-active for the selected state.
  banner: 'dt.marketplace.banner',
  // The reason-aware market-closed banner (frozen / no upcoming slate / not enabled / contest closed).
  pausedBanner: 'dt.marketplace.pausedBanner',
  // Contest page header + controls.
  contest: {
    back: 'dt.marketplace.contest.back',
    sort: 'dt.marketplace.contest.sort',
    shares: 'dt.marketplace.contest.shares',
    // The infinite-scroll sentinel under the Buy-now / All-entries tables.
    loadMore: 'dt.marketplace.contest.loadMore',
  },
  nav: {
    tab: 'dt.marketplace.nav.tab',
    webViewClose: 'dt.marketplace.nav.webViewClose',
  },
  panelTabs: {
    tab: 'dt.marketplace.panelTabs.tab',
  },
  panels: {
    footer: 'dt.marketplace.panels.footer',
  },
  // Pick-history rows: row + its slate label and team alias, for QA's per-row assertions.
  pickHistory: {
    row: 'dt.marketplace.pickHistory.row',
    slate: 'dt.marketplace.pickHistory.slate',
    alias: 'dt.marketplace.pickHistory.alias',
  },
  offers: {
    view: 'dt.marketplace.offers.view',
    buyPill: 'dt.marketplace.offers.buyPill',
    sellPill: 'dt.marketplace.offers.sellPill',
    heading: 'dt.marketplace.offers.heading',
    row: 'dt.marketplace.offers.row',
    skeleton: 'dt.marketplace.offers.skeleton',
    sectionDivider: 'dt.marketplace.offers.sectionDivider',
    loadMore: 'dt.marketplace.offers.loadMore',
    // The entry-panel offer card; distinct from the offers-list row above so a shared id can't
    // match both.
    card: 'dt.marketplace.offers.card',
    rowStatus: 'dt.marketplace.offers.rowStatus',
    acceptButton: 'dt.marketplace.offers.acceptButton',
    counterButton: 'dt.marketplace.offers.counterButton',
    declineButton: 'dt.marketplace.offers.declineButton',
    cancelButton: 'dt.marketplace.offers.cancelButton',
    withdrawButton: 'dt.marketplace.offers.withdrawButton',
    // Accept-offer confirm modal + its "relist the remainder" affordance.
    acceptModal: {
      modal: 'dt.marketplace.offers.acceptModal.modal',
      relist: 'dt.marketplace.offers.acceptModal.relist',
    },
    // Counter-offer modal shell + the form inside it.
    counterModal: {
      modal: 'dt.marketplace.offers.counterModal.modal',
      form: 'dt.marketplace.offers.counterModal.form',
      breakdown: 'dt.marketplace.offers.counterModal.breakdown',
      pickControl: 'dt.marketplace.offers.counterModal.pickControl',
      noMajority: 'dt.marketplace.offers.counterModal.noMajority',
    },
    // Shared "Review your offer" confirm modal, opened from both the listing make-offer form and
    // the direct-offer form before the offer is placed.
    reviewModal: {
      modal: 'dt.marketplace.offers.reviewModal.modal',
      placeOffer: 'dt.marketplace.offers.reviewModal.placeOffer',
      cancel: 'dt.marketplace.offers.reviewModal.cancel',
    },
    // The live buyer-fee breakdown (Offer / Buyer fee / Total) and the relocated wallet balance line
    // beneath the amount input on both offer forms (listing make-offer + direct offer).
    offerForm: {
      breakdown: 'dt.marketplace.offers.offerForm.breakdown',
      balance: 'dt.marketplace.offers.offerForm.balance',
      // The share controls on the make-offer form: each preset button (data-value = its whole
      // percent), the custom-share input, the pick-control transfer/stays indicator, and the band
      // /validation message.
      sharePreset: 'dt.marketplace.offers.offerForm.sharePreset',
      shareInput: 'dt.marketplace.offers.offerForm.shareInput',
      pickControlIndicator: 'dt.marketplace.offers.offerForm.pickControlIndicator',
      shareError: 'dt.marketplace.offers.offerForm.shareError',
      amountError: 'dt.marketplace.offers.offerForm.amountError',
      minimumHelper: 'dt.marketplace.offers.offerForm.minimumHelper',
      useMinimum: 'dt.marketplace.offers.offerForm.useMinimum',
      offersOff: 'dt.marketplace.offers.offerForm.offersOff',
      offersOffBuyNow: 'dt.marketplace.offers.offerForm.offersOffBuyNow',
      offersOffBack: 'dt.marketplace.offers.offerForm.offersOffBack',
      raiseToContinue: 'dt.marketplace.offers.offerForm.raiseToContinue',
    },
    alertSuppressed: 'dt.marketplace.offers.alertSuppressed',
    percentOfEv: 'dt.marketplace.offers.percentOfEv',
    declineModal: {
      modal: 'dt.marketplace.offers.declineModal.modal',
      confirm: 'dt.marketplace.offers.declineModal.confirm',
      keep: 'dt.marketplace.offers.declineModal.keep',
    },
  },
  offerSettings: {
    page: 'dt.marketplace.offerSettings.page',
    saveStatus: 'dt.marketplace.offerSettings.saveStatus',
    resetDefaults: 'dt.marketplace.offerSettings.resetDefaults',
    resetDefaultsList: 'dt.marketplace.offerSettings.resetDefaultsList',
    receiveOffers: 'dt.marketplace.offerSettings.receiveOffers',
    pickControl: 'dt.marketplace.offerSettings.pickControl',
    pickControlInfo: 'dt.marketplace.offerSettings.pickControlInfo',
    evInfo: 'dt.marketplace.offerSettings.evInfo',
    receiveOffersInfo: 'dt.marketplace.offerSettings.receiveOffersInfo',
    alertsInfo: 'dt.marketplace.offerSettings.alertsInfo',
    minimumSwitch: 'dt.marketplace.offerSettings.minimumSwitch',
    minimumInput: 'dt.marketplace.offerSettings.minimumInput',
    alertsSwitch: 'dt.marketplace.offerSettings.alertsSwitch',
    alertInput: 'dt.marketplace.offerSettings.alertInput',
    formError: 'dt.marketplace.offerSettings.formError',
    outcomeBar: 'dt.marketplace.offerSettings.outcomeBar',
    save: 'dt.marketplace.offerSettings.save',
    discard: 'dt.marketplace.offerSettings.discard',
    contestRow: 'dt.marketplace.offerSettings.contestRow',
    contestEdit: 'dt.marketplace.offerSettings.contestEdit',
    contestRemove: 'dt.marketplace.offerSettings.contestRemove',
    emptyAddContest: 'dt.marketplace.offerSettings.emptyAddContest',
    resetExampleInfo: 'dt.marketplace.offerSettings.resetExampleInfo',
    unsavedBar: 'dt.marketplace.offerSettings.unsavedBar',
    saveAndLeave: 'dt.marketplace.offerSettings.saveAndLeave',
    discardAndLeave: 'dt.marketplace.offerSettings.discardAndLeave',
    matchMinimum: 'dt.marketplace.offerSettings.matchMinimum',
    contestSave: 'dt.marketplace.offerSettings.contestSave',
    contestCancel: 'dt.marketplace.offerSettings.contestCancel',
    contestLink: 'dt.marketplace.offerSettings.contestLink',
    contestsEmpty: 'dt.marketplace.offerSettings.contestsEmpty',
    addContestSearch: 'dt.marketplace.offerSettings.addContestSearch',
    addContestOption: 'dt.marketplace.offerSettings.addContestOption',
    addContestLink: 'dt.marketplace.offerSettings.addContestLink',
    back: 'dt.marketplace.offerSettings.back',
  },
  sellerListing: {
    offersEmpty: 'dt.marketplace.sellerListing.offersEmpty',
    // The seller panel's received-offers Load more control and its "Showing N of total"
    // footer.
    loadMore: 'dt.marketplace.sellerListing.loadMore',
    showingCount: 'dt.marketplace.sellerListing.showingCount',
  },
  // Direct-offer flow on the contest page: the All-Entries "Make offer" affordance (unlisted,
  // not-your-own rows) and the DirectOfferForm submit.
  directOffer: {
    makeOfferButton: 'dt.marketplace.directOffer.makeOfferButton',
    // The primary CTA in the direct-offer panel overview (distinct from the All-entries row trigger).
    makeOfferCta: 'dt.marketplace.directOffer.makeOfferCta',
    submit: 'dt.marketplace.directOffer.submit',
  },
  // Edit-listing form.
  editListing: {
    shareInput: 'dt.marketplace.editListing.shareInput',
    priceInput: 'dt.marketplace.editListing.priceInput',
    statusSlot: 'dt.marketplace.editListing.statusSlot',
    expiresEdit: 'dt.marketplace.editListing.expiresEdit',
    expiresInput: 'dt.marketplace.editListing.expiresInput',
    expiryErrorSlot: 'dt.marketplace.editListing.expiryErrorSlot',
    submit: 'dt.marketplace.editListing.submit',
    shareError: 'dt.marketplace.editListing.shareError',
    // The confirm-update modal shown before a listing edit is PATCHed.
    confirmModal: 'dt.marketplace.editListing.confirmModal',
    // The pick-control consequence bullet inside that modal (transfers / stays / unchanged).
    confirmPickControl: 'dt.marketplace.editListing.confirmPickControl',
  },
  // Create-listing flow: the form, review, blocked and success states, per-draft card, the
  // per-field validation errors and the running total-share readout.
  createListing: {
    form: 'dt.marketplace.createListing.form',
    // The CTA that opens the review screen (distinct from `review`, the review screen container).
    openReview: 'dt.marketplace.createListing.openReview',
    review: 'dt.marketplace.createListing.review',
    reviewCard: 'dt.marketplace.createListing.reviewCard',
    // The review's confirm/submit button (commits the drafts).
    submit: 'dt.marketplace.createListing.submit',
    submitError: 'dt.marketplace.createListing.submitError',
    blocked: 'dt.marketplace.createListing.blocked',
    success: 'dt.marketplace.createListing.success',
    draftCard: 'dt.marketplace.createListing.draftCard',
    statusSlot: 'dt.marketplace.createListing.statusSlot',
    shareInvalidError: 'dt.marketplace.createListing.shareInvalidError',
    priceInvalidError: 'dt.marketplace.createListing.priceInvalidError',
    exceedsAvailableError: 'dt.marketplace.createListing.exceedsAvailableError',
    noMajorityError: 'dt.marketplace.createListing.noMajorityError',
    expiryError: 'dt.marketplace.createListing.expiryError',
    expiryErrorSlot: 'dt.marketplace.createListing.expiryErrorSlot',
    totalShare: 'dt.marketplace.createListing.totalShare',
  },
  // Cancel-listing confirm modal.
  cancelListing: {
    modal: 'dt.marketplace.cancelListing.modal',
  },
  listingDetail: {
    buyNowButton: 'dt.marketplace.listingDetail.buyNowButton',
    offerDetailTab: 'dt.marketplace.listingDetail.offerDetailTab',
    tabOtherListings: 'dt.marketplace.listingDetail.tabOtherListings',
    cancelOfferButton: 'dt.marketplace.listingDetail.cancelOfferButton',
    acceptCounterButton: 'dt.marketplace.listingDetail.acceptCounterButton',
    rejectCounterButton: 'dt.marketplace.listingDetail.rejectCounterButton',
  },
  // Other open listings on the same entry (all sellers), shown on the Listing-detail tab.
  otherListings: {
    card: 'dt.marketplace.otherListings.card',
  },
  tableSkeleton: 'dt.marketplace.tableSkeleton',
  listingsTableSkeleton: 'dt.marketplace.listingsTableSkeleton',
  panel: {
    skeleton: 'dt.marketplace.panel.skeleton',
  },
  // The entry-based Selling panel: its Listing tab's active listings and per-listing actions,
  // plus the "list the remainder" affordance.
  entryPanel: {
    // The selling panel's tab switcher; each button's id is suffixed by its value
    // (entryPanel.tab + '-' + 'entry' | 'listing' | 'offers') so QA can reach the Listing tab
    // (Edit/Cancel) and the Offers tab (Accept/Decline/Counter) headlessly.
    tab: 'dt.marketplace.entryPanel.tab',
    listings: 'dt.marketplace.entryPanel.listings',
    listingCard: 'dt.marketplace.entryPanel.listingCard',
    listingEdit: 'dt.marketplace.entryPanel.listingEdit',
    listingCancel: 'dt.marketplace.entryPanel.listingCancel',
    listRemaining: 'dt.marketplace.entryPanel.listRemaining',
    // The list-remaining CTA's pinned-footer home: tab-independent, below the scrolling body, so the
    // CTA never sits below the fold. Present only when the CTA is offered.
    listRemainingFooter: 'dt.marketplace.entryPanel.listRemainingFooter',
    // The scrolling body region (overflow-y auto); the footer CTA is a card child OUTSIDE it.
    body: 'dt.marketplace.entryPanel.body',
    // The panel's received-offers card list; its own id so it can't collide with the Offers page's
    // offers.view root, which renders behind it on /marketplace/offers?entry=<id>.
    offerCards: 'dt.marketplace.entryPanel.offerCards',
  },
  // The lobby's contest-discovery carousel; `loadMore` is its trailing infinite-scroll sentinel.
  lobby: {
    loadMore: 'dt.marketplace.lobby.loadMore',
    cardStats: 'dt.marketplace.lobby.cardStats',
    carousel: 'dt.marketplace.lobby.carousel',
    recentSales: 'dt.marketplace.lobby.recentSales',
    recentSale: 'dt.marketplace.lobby.recentSale',
  },
  grid: {
    card: 'dt.marketplace.grid.card',
    contestLink: 'dt.marketplace.grid.contestLink',
    listingRow: 'dt.marketplace.grid.listingRow',
    noListings: 'dt.marketplace.grid.noListings',
    skeleton: 'dt.marketplace.grid.skeleton',
    loadMore: 'dt.marketplace.grid.loadMore',
  },
  // Contest-browse tables: the all-entries table row and the Buy-now listings table row.
  entries: {
    row: 'dt.marketplace.entries.row',
  },
  listings: {
    row: 'dt.marketplace.listings.row',
  },
  sales: {
    row: 'dt.marketplace.sales.row',
    panel: 'dt.marketplace.sales.panel',
    teamUsed: 'dt.marketplace.sales.teamUsed',
  },
  stats: {
    card: 'dt.marketplace.stats.card',
  },
  // Portfolio: the holdings table row (+ its pending-offers badge) and the my-listings table
  // row, its expanded sub-row and the nested per-listing table. `count` is the header total,
  // `loadMore` the paging control and `showingCount` the "Showing N of total" footer.
  holdings: {
    row: 'dt.marketplace.holdings.row',
    offersBadge: 'dt.marketplace.holdings.offersBadge',
    count: 'dt.marketplace.holdings.count',
    loadMore: 'dt.marketplace.holdings.loadMore',
    showingCount: 'dt.marketplace.holdings.showingCount',
    skeleton: 'dt.marketplace.holdings.skeleton',
    // The contest-filter control, so QA can drive the selection instead of a URL param: the
    // "+ Filter by contest" trigger, the search box, each contest option, Clear and Apply.
    filterTrigger: 'dt.marketplace.holdings.filterTrigger',
    filterSearch: 'dt.marketplace.holdings.filterSearch',
    filterOption: 'dt.marketplace.holdings.filterOption',
    filterClear: 'dt.marketplace.holdings.filterClear',
    filterApply: 'dt.marketplace.holdings.filterApply',
  },
  partnerOffers: {
    draftsScrollTable: 'dt.marketplace.partnerOffers.draftsScrollTable',
  },
  myListings: {
    row: 'dt.marketplace.myListings.row',
    subRow: 'dt.marketplace.myListings.subRow',
    nestedTable: 'dt.marketplace.myListings.nestedTable',
    count: 'dt.marketplace.myListings.count',
    loadMore: 'dt.marketplace.myListings.loadMore',
    showingCount: 'dt.marketplace.myListings.showingCount',
    // The info affordance on a closed row whose cancellation reason is shown in a tooltip.
    closedReason: 'dt.marketplace.myListings.closedReason',
  },
  // Pick-display: revealed-picks surfaces — the team chip, the entry-info available-teams
  // section, the entries team-picks matrix, and the filter-by-available-teams control.
  pickDisplay: {
    teamChip: 'dt.marketplace.pickDisplay.teamChip',
    availableTeams: 'dt.marketplace.pickDisplay.availableTeams',
    matrix: 'dt.marketplace.pickDisplay.matrix',
    // The matrix's per-entry <tr>; its own id so it never shares one with the offers-list row.
    matrixRow: 'dt.marketplace.pickDisplay.matrixRow',
    filter: 'dt.marketplace.pickDisplay.filter',
    filterOption: 'dt.marketplace.pickDisplay.filterOption',
    filterSearch: 'dt.marketplace.pickDisplay.filterSearch',
    filterClear: 'dt.marketplace.pickDisplay.filterClear',
    filterApply: 'dt.marketplace.pickDisplay.filterApply',
    showTeamPicks: 'dt.marketplace.pickDisplay.showTeamPicks',
    selectedTeamPill: 'dt.marketplace.pickDisplay.selectedTeamPill',
    entriesFound: 'dt.marketplace.pickDisplay.entriesFound',
    // The pick-control key badge shown on a row's controller (matrix + entries table).
    pickControllerBadge: 'dt.marketplace.pickDisplay.pickControllerBadge',
  },
} as const;
